"use client";

import { useRouter } from "next/router";
import Script from "next/script";
import { signIn, useSession } from "next-auth/react";
import {
  useCallback,
  useEffect,
  useEffectEvent,
  useSyncExternalStore,
} from "react";
import { toast } from "sonner";
import { env } from "@/env.mjs";
import {
  getNextAuthSignInErrorMessage,
  OAUTH_ACCOUNT_NOT_LINKED_ERROR,
} from "@/lib/auth/auth-error-codes";
import { getClientPostAuthDestination } from "@/lib/auth/callback-url";
import {
  GOOGLE_ONE_TAP_PROVIDER_ID,
  isGoogleOneTapAllowedPath,
} from "@/lib/auth/google-one-tap";
import {
  type GoogleOneTapCredentialResponse,
  getGoogleOneTapClient,
  getGoogleOneTapScriptReadyServerSnapshot,
  getGoogleOneTapScriptReadySnapshot,
  markGoogleOneTapScriptReady,
  subscribeGoogleOneTapScriptReady,
} from "@/lib/auth/google-one-tap-client";

const GOOGLE_ONE_TAP_SCRIPT_SRC = "https://accounts.google.com/gsi/client";
let isGoogleOneTapSignInInFlight = false;
let lastGoogleOneTapPath: string | null = null;

function getPathFromAsPath(asPath: string): string {
  const pathWithHash = asPath.split("?")[0] ?? "/";
  return pathWithHash.split("#")[0] ?? "/";
}

function getSameOriginRouterDestination(url: string): string {
  const parsed = new URL(url, window.location.origin);
  return `${parsed.pathname}${parsed.search}${parsed.hash}`;
}

function getCurrentPostAuthDestination(): string {
  return getClientPostAuthDestination();
}

function useGoogleOneTapScriptReady() {
  return useSyncExternalStore(
    subscribeGoogleOneTapScriptReady,
    getGoogleOneTapScriptReadySnapshot,
    getGoogleOneTapScriptReadyServerSnapshot
  );
}

function useGoogleOneTapPrompt(params: {
  clientId: string;
  eligiblePath: string;
  onCredential: (response: GoogleOneTapCredentialResponse) => void;
  shouldPrompt: boolean;
}) {
  // react-doctor-disable-next-line react-doctor/no-event-handler -- onCredential callback is stabilized via useEffectEvent and invoked inside the effect for Google One Tap init
  const { clientId, eligiblePath, onCredential, shouldPrompt } = params;
  const activeEligiblePath = shouldPrompt ? eligiblePath : "";
  const handleCredential = useEffectEvent(onCredential);

  // biome-ignore lint/correctness/useExhaustiveDependencies: handleCredential is a stable useEffectEvent and must not be listed as a dependency
  useEffect(() => {
    const googleOneTapClient = getGoogleOneTapClient();
    if (!activeEligiblePath) {
      lastGoogleOneTapPath = null;
      return;
    }

    if (!googleOneTapClient) {
      return;
    }

    if (lastGoogleOneTapPath === activeEligiblePath) {
      return;
    }

    if (
      googleOneTapClient.initialize({
        clientId,
        onCredential: (response) => {
          handleCredential(response);
        },
      })
    ) {
      lastGoogleOneTapPath = activeEligiblePath;
      googleOneTapClient.prompt();
    }
  }, [activeEligiblePath, clientId]);
}

export function GoogleOneTap() {
  const { asPath, push } = useRouter();
  const { status } = useSession();
  const isScriptReady = useGoogleOneTapScriptReady();

  const currentPath = getPathFromAsPath(asPath);
  const isAllowedRoute = isGoogleOneTapAllowedPath(currentPath);
  const shouldPrompt =
    isScriptReady && status === "unauthenticated" && isAllowedRoute;

  const redirectToAccountLinkingError = useCallback(
    async (postAuthDestination: string) => {
      const destination = new URL("/login", window.location.origin);
      destination.searchParams.set("error", OAUTH_ACCOUNT_NOT_LINKED_ERROR);
      destination.searchParams.set("callbackUrl", postAuthDestination);

      await push(
        `${destination.pathname}${destination.search}${destination.hash}`
      );
    },
    [push]
  );

  const handleCredentialResponse = useCallback(
    async (response: GoogleOneTapCredentialResponse) => {
      if (!(response.credential && !isGoogleOneTapSignInInFlight)) {
        return;
      }

      const postAuthDestination = getCurrentPostAuthDestination();
      isGoogleOneTapSignInInFlight = true;

      try {
        const signInResponse = await signIn(GOOGLE_ONE_TAP_PROVIDER_ID, {
          credential: response.credential,
          redirect: false,
          redirectTo: postAuthDestination,
        });

        if (signInResponse?.code === OAUTH_ACCOUNT_NOT_LINKED_ERROR) {
          await redirectToAccountLinkingError(postAuthDestination);
          return;
        }

        if (signInResponse?.error) {
          toast.error("Uh oh! Something went wrong.", {
            description:
              getNextAuthSignInErrorMessage(signInResponse.error) ??
              "Failed to sign in with Google. Try again.",
          });
          return;
        }

        if (signInResponse?.url) {
          await push(getSameOriginRouterDestination(signInResponse.url));
        }
      } catch (_error) {
        toast.error("Uh oh! Something went wrong.", {
          description: "Failed to sign in with Google. Try again.",
        });
      } finally {
        isGoogleOneTapSignInInFlight = false;
      }
    },
    [push, redirectToAccountLinkingError]
  );

  const handleScriptReady = useCallback(() => {
    markGoogleOneTapScriptReady();
  }, []);

  useGoogleOneTapPrompt({
    clientId: env.NEXT_PUBLIC_AUTH_GOOGLE_ID,
    eligiblePath: currentPath,
    onCredential: handleCredentialResponse,
    shouldPrompt,
  });

  return (
    <Script
      id="google-one-tap-client"
      onReady={handleScriptReady}
      src={GOOGLE_ONE_TAP_SCRIPT_SRC}
      strategy="afterInteractive"
    />
  );
}
